/**
 * Content model and copy for the /ai-teammates SEO hub page.
 * The content is intentionally long-form so the route can target high-intent
 * category keywords while remaining readable and structured.
 */

export interface AiTeammatesSubsection {
	id: string;
	title: string;
	paragraphs: string[];
	orderedList?: string[];
	unorderedList?: string[];
}

export interface AiTeammatesSection {
	id: string;
	title: string;
	subsections: AiTeammatesSubsection[];
}

export interface AiTeammatesFaqItem {
	id: string;
	question: string;
	answer: string;
}

export interface AiTeammatesInternalLink {
	to:
		| "/ai-teammates/marketing"
		| "/ai-teammates/marketing/google-ads"
		| "/ai-teammates/marketing/seo";
	label: string;
	description: string;
}

export interface AiTeammatesExternalLink {
	href: string;
	label: string;
	description: string;
}

export const AI_TEAMMATES_PAGE_TITLE = "AI Teammates for Marketing & Ops";
export const AI_TEAMMATES_META_DESCRIPTION =
	"Deploy AI teammates that own recurring marketing and operations work—monitoring, reporting, and triage on autopilot with human oversight.";

export const AI_TEAMMATES_H1 = "AI Teammates That Actually Do the Work";

export const AI_TEAMMATES_INTRO_PARAGRAPHS = [
	"AI teammates are autonomous operators, not another chat tab. They connect to your tools, run on schedule, and deliver structured output with clear guardrails.",
	"Most teams don't have a strategy problem. They have an execution problem — monitoring drifts, reports arrive late, decisions happen without clean context.",
	"Start with one workflow. Prove it works. Then scale.",
];

export const AI_TEAMMATES_OUTLINE = [
	"What AI teammates are and where they fit",
	"How AI teammates run in real operations",
	"Marketing workflows for PPC, SEO, and reporting",
	"AI teammates vs tools, agencies, and hiring",
	"Implementation framework for week-one launch",
	"Rollout mistakes and practical fixes",
	"References for policy, measurement, and trust",
	"Helpful m8tes pages for rollout planning",
	"FAQ on reliability, permissions, and ROI",
	"Final launch checklist and next step",
];

export const AI_TEAMMATES_SECTIONS: AiTeammatesSection[] = [
	{
		id: "what-are-ai-teammates",
		title: "What Are AI Teammates?",
		subsections: [
			{
				id: "operators-not-chat-tabs",
				title: "Operators, not chat tabs",
				paragraphs: [
					"A teammate owns a recurring outcome — campaign health checks, weekly reporting prep, anomaly triage. That ownership is what separates an autonomous AI agent from a standard assistant that only responds when someone asks a question.",
					"The language matters. Frame the system as an assistant and people treat it like a search box. Frame it as a teammate and people delegate work with clear expectations, deadlines, and constraints. Better instructions, better accountability, fewer abandoned automation experiments.",
				],
			},
			{
				id: "fit-in-small-teams",
				title: "Where AI teammates fit in lean teams",
				paragraphs: [
					"In small and mid-sized teams, the highest-value work gets interrupted by operational drag. Someone has to pull channel metrics, clean spreadsheets, answer stakeholder questions, and double-check platform settings. AI teammates for business reduce that drag by taking over repeatable operational loops so humans can focus on strategy, creative direction, and final decisions.",
					"The strongest outcomes happen when the role is framed as augmentation, not replacement. AI workers handle repetitive execution and first-pass analysis, while humans keep context, judgment, and prioritization. This split creates a healthier operating rhythm — no one is forced to choose between deep work and constant maintenance.",
				],
			},
			{
				id: "start-signals",
				title: "Signals that it is time to launch one",
				paragraphs: [
					"Start when the same task repeats every week, affects money or customer outcomes, and still slips through the cracks. That pattern means the workflow is important enough to deserve structure — and an AI teammate can generate immediate value without a long change-management process.",
				],
				unorderedList: [
					"Your team repeats the same reporting process every week but still ships late.",
					"Important account checks depend on one person remembering to run them.",
					"You pay for data tools but insight delivery is inconsistent.",
					"The team spends more time formatting updates than deciding what to do next.",
					"You know what to execute, but there is no spare bandwidth to do it.",
				],
			},
		],
	},
	{
		id: "how-it-works",
		title: "How AI teammates work in a real business",
		subsections: [
			{
				id: "connect-stack",
				title: "Step 1: Connect your existing stack",
				paragraphs: [
					"No need to replace your stack. Connect the tools your team already trusts and assign one narrow workflow. This keeps risk low and adoption high. For marketing teams, that usually starts with Google Ads, Google Sheets, and Google Search Console because they hold the data that drives weekly decisions.",
					"The right architecture feels boring in the best way. OAuth permissions are clear, data access is explicit, and every action is traceable. An AI agent platform should give you visibility into what happened, when it happened, and why — so you can coach performance the same way you coach a human teammate.",
				],
				orderedList: [
					"Connect one data source and one destination first.",
					"Set permission scope based on least privilege.",
					"Verify outputs in a low-risk environment before scaling.",
				],
			},
			{
				id: "define-outcomes",
				title: "Step 2: Delegate outcomes, not vague tasks",
				paragraphs: [
					'AI teammates improve fast when instructions describe outcomes and boundaries instead of abstract goals. Avoid prompts like "improve our campaigns" — they hide assumptions. Write operational briefs that include the data source, time range, thresholds, required format, escalation rule, and destination for the final output.',
					"This approach makes autonomous AI agents easier to manage because success is measurable. If the teammate misses a threshold, fails to include required context, or sends an incomplete summary, you can adjust instructions with precision. The feedback loop becomes operational coaching rather than trial-and-error prompt tweaking.",
				],
				orderedList: [
					"Name the business question the teammate must answer.",
					"Define exact inputs and the allowed tools.",
					"Set pass or fail criteria for output quality.",
					"Specify who gets notified and in what format.",
				],
			},
			{
				id: "run-and-review",
				title: "Step 3: Run on schedule and review quickly",
				paragraphs: [
					"Most of the value comes from consistency, not novelty. Run AI teammates on weekly and daily cadences for monitoring workflows because delayed execution is the expensive part. Once the schedule is stable, use a short review loop to check factual accuracy, decision relevance, and whether escalations arrived early enough to matter.",
					"If a run fails or produces weak output, treat it as an operating issue. Do a small postmortem, tighten the instructions, and rerun the task with the same inputs. This process builds trust quickly because teammates improve in visible increments instead of requiring a full redesign every time something goes wrong.",
				],
				unorderedList: [
					"Use fixed run times for recurring workflows.",
					"Keep one owner accountable for review and iteration.",
					"Track error categories so instruction updates are targeted.",
					"Escalate only when action is required, not for every datapoint.",
				],
			},
		],
	},
	{
		id: "marketing-use-cases",
		title: "AI Teammates for Marketing Teams",
		subsections: [
			{
				id: "ppc-use-case",
				title: "PPC execution and AI for Google Ads management",
				paragraphs: [
					"If you run paid search, AI teammates for marketing can monitor spend volatility, conversion drops, and impression-share movement before those changes become expensive. Think of it as a first-line operating layer that watches campaigns continuously and flags conditions that deserve human review. One of the cleanest paths to value because the feedback cycle is short and measurable.",
					"This workflow also reduces reporting lag. Instead of waiting for manual pull requests from analysts, the teammate compiles structured summaries with account-level and campaign-level context. For teams comparing automated PPC management or a general Google Ads AI tool, the differentiator is execution continuity, not just dashboards.",
				],
				unorderedList: [
					"Detect unusual spend acceleration before budget is exhausted.",
					"Highlight campaign groups with sudden conversion rate changes.",
					"Summarize top winners and top wasters every reporting cycle.",
					"Prepare decision-ready notes for weekly optimization meetings.",
					"Route urgent anomalies to Slack or email with clear context.",
				],
			},
			{
				id: "seo-use-case",
				title: "SEO monitoring with an AI SEO agent",
				paragraphs: [
					"An AI SEO agent is useful when rankings and indexing health need consistent oversight but the team cannot justify full-time manual checks. AI teammates can monitor query movement, spot pages with unusual click-through decline, and flag indexing shifts that need immediate follow-up. This reduces the chance that important organic trends stay hidden for weeks.",
					"For teams exploring an AI search console agent, start with a focused mandate: detect meaningful changes and summarize probable causes. That scope prevents overreach and keeps the output actionable. Once reliability is proven, the teammate can expand into recommendation drafting and recurring content update prioritization.",
				],
				unorderedList: [
					"Track meaningful ranking movement for priority keyword clusters.",
					"Alert when high-value pages lose clicks or impressions unexpectedly.",
					"Identify indexing and coverage changes that need investigation.",
					"Produce weekly summaries that connect trend shifts to actions.",
				],
			},
			{
				id: "reporting-use-case",
				title: "Reporting cadence without spreadsheet fatigue",
				paragraphs: [
					"Reporting is one of the most overlooked leverage points in AI marketing automation. Teams often spend hours gathering numbers and very little time interpreting them. AI teammates collect metrics, normalize naming, draft a narrative, and deliver updates in a predictable format so meetings start with decisions instead of data cleanup.",
					"When this is done well, stakeholders trust the cadence. They know the update will arrive on time, include the same core fields, and highlight deviations that matter. This is where AI powered marketing automation helps operational maturity: consistency turns communication into a system, not a scramble.",
				],
				orderedList: [
					"Pull source data from connected channels on a fixed schedule.",
					"Standardize naming and group metrics by business objective.",
					"Draft plain-language insight summaries with notable deltas.",
					"Send a final report package to the team channel and archive.",
				],
			},
		],
	},
	{
		id: "comparison",
		title: "AI Teammates vs Other Approaches",
		subsections: [
			{
				id: "vs-tools",
				title: "AI teammates vs AI tools and copilots",
				paragraphs: [
					"Most AI tools help with ideation and drafting, which is useful, but that alone does not fix operational bottlenecks. Someone still needs to run checks, publish updates, and close loops across systems. AI teammates add that missing operating layer by executing recurring tasks with explicit ownership and measurable outcomes.",
					"Use tools for one-off assistance and teammates for repeatable production work. That separation keeps expectations clean. If a workflow affects campaign spend, pipeline health, or reporting reliability, assign it to a teammate so execution does not depend on who remembered to open the right dashboard.",
				],
			},
			{
				id: "vs-agencies",
				title: "AI teammates vs agency dependency",
				paragraphs: [
					"Agencies can be excellent strategic partners, but many teams rely on them for routine operational tasks that could run continuously in-house. AI teammates handle recurring monitoring and prep work so agency time is reserved for high-value strategy and major experiments. This model improves speed while protecting expertise where it matters most.",
					"The cost profile also changes. Instead of paying for basic workflow execution in every monthly retainer cycle, you automate foundational work and purchase specialized support only when needed. For teams evaluating an AI marketing agency alternative, this hybrid structure often delivers better control and faster iteration.",
				],
			},
			{
				id: "vs-hiring",
				title: "AI teammates vs immediate hiring",
				paragraphs: [
					"Hiring remains essential for leadership, creative ownership, and complex cross-functional judgment. Where AI teammates help is capacity planning during growth periods, when workload expands faster than headcount. They absorb repetitive execution so the existing team can maintain quality while hiring decisions stay deliberate.",
					"Treat this as a risk-management choice, not a replacement narrative. If the process is unstable, adding headcount into chaos does not solve the root problem. Building an AI workforce for recurring operations first creates cleaner systems, clearer handoffs, and better onboarding conditions for future hires.",
				],
				unorderedList: [
					"Keep strategic thinking and final approvals with human owners.",
					"Use AI teammates for repeatable execution and first-pass analysis.",
					"Document standard workflows before adding more people.",
					"Scale headcount after operational baselines are stable.",
				],
			},
		],
	},
	{
		id: "implementation-framework",
		title: "Implementation Framework: Launch Your First AI Teammate",
		subsections: [
			{
				id: "pick-workflow",
				title: "Choose one workflow with clear economics",
				paragraphs: [
					"Start with one workflow that is frequent, measurable, and tied to a meaningful business outcome. Good examples: daily budget anomaly checks, weekly SEO movement summaries, Monday reporting prep. These tasks repeat often enough to justify system design and produce value quickly enough to build internal momentum.",
					'Avoid broad mandates early. A teammate should not start with "own all marketing operations." Narrow scopes make quality easier to validate, reduce onboarding complexity, and create a template the team can reuse. Once the first workflow is stable, expansion is straightforward because the operating model already exists.',
				],
			},
			{
				id: "write-instructions",
				title: "Write instructions that are hard to misunderstand",
				paragraphs: [
					"Instruction quality determines teammate quality. Write briefs that specify data sources, allowed tools, thresholds, escalation channels, and required output format. This is less about prompt artistry and more about operational clarity. The clearer the operating spec, the faster the teammate becomes dependable.",
					"Define unacceptable behavior explicitly. If a metric is missing, if confidence is low, or if the system cannot access a required source, the teammate should escalate instead of guessing. This simple rule prevents silent errors and protects trust while automation volume increases.",
				],
				orderedList: [
					"State the objective and who the output is for.",
					"List exact sources and the permitted date range.",
					"Define thresholds that trigger warnings or escalation.",
					"Specify output structure and delivery destination.",
					"Document fallback behavior when data is incomplete.",
				],
			},
			{
				id: "set-guardrails",
				title: "Add guardrails before scaling volume",
				paragraphs: [
					"Before increasing run frequency, add controls for reliability, auditability, and communication. Every task should have a clear owner, a review path, and a rollback habit. Even when teammates are read-only, a disciplined review loop catches drift early and keeps decision quality high.",
					"Guardrails are also cultural. Teams adopt automation faster when they understand who approves outputs and how exceptions are handled. Keep this lightweight but explicit. A short checklist and a weekly review are usually enough to prevent confusion as more teammates come online.",
				],
				unorderedList: [
					"Assign one accountable owner per teammate workflow.",
					"Version control instruction changes with short notes.",
					"Log run outcomes and categorize failure reasons.",
					"Review false positives and false negatives weekly.",
					"Escalate uncertain outputs instead of forcing automation.",
				],
			},
		],
	},
	{
		id: "mistakes",
		title: "Common Mistakes and Practical Fixes",
		subsections: [
			{
				id: "mistake-scope",
				title: "Mistake 1: Starting too broad",
				paragraphs: [
					"The fastest way to lose trust is asking one teammate to handle too many workflows at once. Broad scopes create inconsistent output because each task has different success criteria. Fix this by splitting responsibilities into small operating units, each with one measurable objective and one owner.",
					"A focused scope also improves iteration speed. When an output misses the mark, you can trace the issue to one instruction set, one data dependency, or one threshold definition. That precision turns debugging into routine operations work instead of a large redesign effort.",
				],
			},
			{
				id: "mistake-metrics",
				title: "Mistake 2: Tracking activity instead of outcomes",
				paragraphs: [
					"Teams sometimes measure success by number of runs or number of generated reports. Those metrics describe activity, not value. Track leading indicators that connect directly to business impact: anomaly detection lead time, reporting timeliness, and reduction in manual preparation hours.",
					"Outcome metrics keep stakeholder conversations practical. If the teammate produces ten reports but none changed a decision, the workflow needs redesign. If one weekly run consistently surfaces useful decisions, that workflow deserves expansion. This framing keeps AI teammates aligned with business priorities.",
				],
			},
			{
				id: "mistake-feedback",
				title: "Mistake 3: No consistent feedback loop",
				paragraphs: [
					"Automation degrades when no one closes the loop. Fix this with a short recurring review that covers output quality, exception handling, and instruction updates. The cadence can be 20 minutes per week, but it must happen. Without it, small issues compound until confidence drops.",
					"Keep a simple change log for prompts and thresholds so improvements stay explainable. This helps new team members understand why a workflow behaves a certain way and prevents regression when ownership changes. A lightweight operating rhythm is usually enough to maintain quality over time.",
				],
				orderedList: [
					"Review the latest outputs against the defined success criteria.",
					"Label misses by cause: data, instruction, or threshold.",
					"Apply one targeted change and rerun the workflow.",
					"Record the result and keep changes that improve reliability.",
				],
			},
		],
	},
];

export const AI_TEAMMATES_EXTERNAL_LINKS: AiTeammatesExternalLink[] = [
	{
		href: "https://www.anthropic.com/research/building-effective-agents",
		label: "Building effective agents — Anthropic",
		description: "Anthropic's guide to building reliable AI agent systems.",
	},
	{
		href: "https://openai.com/index/practices-for-governing-agentic-ai-systems/",
		label: "Practices for governing agentic AI systems — OpenAI",
		description: "OpenAI's framework for safe and accountable AI agent operations.",
	},
	{
		href: "https://cloud.google.com/blog/products/ai-machine-learning/the-next-chapter-of-ai-agents",
		label: "The next chapter of AI agents — Google Cloud",
		description: "Google Cloud's vision for production AI agent infrastructure.",
	},
];

export const AI_TEAMMATES_INTERNAL_LINKS: AiTeammatesInternalLink[] = [
	{
		to: "/ai-teammates/marketing",
		label: "AI teammates for marketing",
		description:
			"Go deeper on SEO, PPC, Google Ads, Search Console, and email marketing workflows.",
	},
	{
		to: "/ai-teammates/marketing/google-ads",
		label: "Google Ads AI teammate",
		description: "Read the full Google Ads campaign management and anomaly monitoring breakdown.",
	},
	{
		to: "/ai-teammates/marketing/seo",
		label: "AI SEO teammate",
		description:
			"Read the Search Console, Keyword Planner, and automated SEO reporting implementation guide.",
	},
];

export const AI_TEAMMATES_FAQ_ITEMS: AiTeammatesFaqItem[] = [
	{
		id: "faq-definition",
		question: "What is an AI teammate, exactly?",
		answer:
			"An AI teammate is an autonomous agent that owns repeatable work. You delegate clear outcomes, connected tools, and escalation rules — then the teammate runs on schedule and ships results. If the task repeats and quality can be measured, a teammate is a strong fit.",
	},
	{
		id: "faq-difference",
		question: "How is this different from a normal AI tool?",
		answer:
			"A normal AI tool helps in-session and waits for the next prompt. An AI teammate runs operational workflows in the background with explicit ownership and a recurring cadence. Use tools for ad hoc drafting and ideation, teammates for ongoing execution loops.",
	},
	{
		id: "faq-marketing",
		question: "What marketing tasks can AI teammates handle today?",
		answer:
			"Start with high-frequency, high-friction tasks: campaign health monitoring, anomaly detection, weekly performance summaries, and SEO movement checks. Teams also automate recurring report preparation and stakeholder updates via Sheets, Slack, and email. Narrow scope first, expand once the baseline is stable.",
	},
	{
		id: "faq-trust",
		question: "Can I trust an AI teammate with sensitive workflows?",
		answer:
			"Yes, if you design for controlled access and review. Apply least-privilege permissions, define clear escalation behavior, and keep one owner accountable for output checks. Start with read-focused workflows, then expand as reliability improves.",
	},
	{
		id: "faq-roi",
		question: "How should I measure ROI from AI teammates?",
		answer:
			"Measure value using outcome metrics, not run counts. Useful baselines: time saved on reporting preparation, lead time to detect channel anomalies, and consistency of weekly operational output. Tie those gains to labor hours and performance improvements for a defensible ROI story.",
	},
	{
		id: "faq-start",
		question: "What is the fastest way to get started this week?",
		answer:
			"Pick one recurring workflow with a clear owner and measurable output. Connect only the required tools, write a tight operating brief, and schedule one recurring run. Review the first few outputs, adjust thresholds and formatting, and you will see useful results in days.",
	},
];

export const AI_TEAMMATES_FAQ_SCHEMA_ITEMS = AI_TEAMMATES_FAQ_ITEMS.map((item) => ({
	question: item.question,
	answer: item.answer,
}));
